import type { KeyboardEvent } from "react";
import type { CogentActivity } from "@softmax/avatar/character";
import type { useFace } from "@softmax/avatar/expressions";
import { cornerForKey } from "./placement/widget-corner";
import { readMountedElement } from "./read-mounted-element";
import type { CogentWidgetProps, CogentPresence } from "./widget-types";
import type { WidgetPosition } from "./placement/use-widget-position";
import type { useWidgetCharacter } from "./use-widget-character";
import s from "./cogent.module.css";
function moveByKeyboard({
  event,
  position,
}: {
  event: KeyboardEvent;
  position: WidgetPosition;
}) {
  if (!event.altKey) return;

  const next = cornerForKey({ corner: position.corner, key: event.key });

  if (!next) return;

  event.preventDefault();
  position.moveTo({
    next,
    from: readMountedElement(position.dock).getBoundingClientRect(),
  });
}
export function WidgetCharacter({
  props,
  character,
  position,
  ids,
  name,
  state,
  face,
  activity,
  open,
  toggle,
  status,
}: {
  props: CogentWidgetProps;
  character: ReturnType<typeof useWidgetCharacter>;
  position: WidgetPosition;
  ids: string;
  name: string;
  state: CogentPresence;
  face: ReturnType<typeof useFace>["face"];
  activity: CogentActivity;
  open: boolean;
  toggle: () => void;
  status: string | null;
}) {
  const Character = props.character;
  const { clip, facing, dock: dockRef, avatar: avatarRef } = position;
  return (
    <div
      ref={dockRef}
      className={s.stand}
      data-testid="cogent-dock"
      data-cogent-orb
    >
      {status ? (
        <output className={s.standStatus} data-testid="cogent-stand-status">
          <span className={s.thinking}>{status}</span>
        </output>
      ) : null}
      {character.drawn === "loading" ? (
        <span
          className={s.loading}
          aria-hidden="true"
          data-testid="cogent-loading"
        />
      ) : null}
      {character.drawn === "failed" ? (
        <button
          type="button"
          className={s.retry}
          data-testid="cogent-retry"
          onClick={character.retry}
        >
          Couldn&rsquo;t show {name}. Retry
        </button>
      ) : null}
      {!character.settled || character.wearing === undefined ? null : (
        <Character
          key={character.attempt}
          onLoad={character.setDrawn}
          cogentId={props.cogent?.id ?? ""}
          name={name}
          assetsBaseUrl={props.assetsBaseUrl}
          look={character.wearing}
          muted={!props.cogent}
          expression={face}
          state={clip ? "hover" : "idle"}
          clip={clip}
          activity={activity}
          facing={facing}
          onClipEnd={() => position.setClip(null)}
          onFigure={character.onFigure}
        />
      )}
      <button
        ref={avatarRef}
        type="button"
        className={s.avatar}
        aria-expanded={open}
        aria-controls={`${ids}-box`}
        aria-label={`Talk to ${name}`}
        aria-describedby={`${ids}-state`}
        aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown Alt+ArrowLeft Alt+ArrowRight"
        data-testid="cogent-avatar"
        onKeyDown={(event) =>
          moveByKeyboard({
            event,
            position,
          })
        }
        onClick={(event) => {
          if (position.consumeDrag()) event.preventDefault();
          else toggle();
        }}
        onPointerEnter={(event) => {
          if (
            event.pointerType === "mouse" &&
            window.matchMedia("(hover: hover)").matches
          )
            position.playClip();
        }}
        onPointerLeave={position.leave}
        onFocus={(event) => {
          if (event.currentTarget.matches(":focus-visible"))
            position.playClip();
        }}
        onBlur={() => position.setClip(null)}
        onPointerDown={position.onPointerDown}
        onDragStart={(event) => event.preventDefault()}
      >
        <span id={`${ids}-state`} className={s.srOnly}>
          {state}
        </span>
      </button>
    </div>
  );
}
